import { io, type Socket } from 'socket.io-client';
import type { DomainEventEnvelope } from '@bhops/shared/realtime';

// One shared socket per session. The access token is passed in the
// socket.io `auth` handshake — never in the query string — matching the
// backend gateway's JWT middleware (QC-SEC-03).
//
// Reconnection is automatic with exponential backoff. On access-token
// refresh we disconnect+reconnect so the server re-authenticates with the
// fresh credential.
//
// Wire format: every server event ships on the single 'domain' channel
// with a typed envelope. Listeners receive `DomainEventEnvelope` and
// narrow on `body.eventType` via `isDomainEvent` from
// `@bhops/shared/realtime`. See `docs/architecture/realtime-protocol.md`
// for the full contract.

let socket: Socket | null = null;
const listeners = new Set<(event: DomainEventEnvelope) => void>();

function baseUrl(): string {
  // Vite proxies /api during dev; for WS we target the API server directly
  // because socket.io needs a full origin. In prod the reverse-proxy exposes
  // both HTTP and WS under the same host, so same-origin works everywhere.
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv) return new URL(fromEnv).origin;
  // Default: API on :3000 during Vite dev.
  if (typeof window !== 'undefined' && window.location.port === '5173') {
    return `${window.location.protocol}//${window.location.hostname}:3000`;
  }
  return window.location.origin;
}

export function connectRealtime(accessToken: string): Socket {
  if (socket && socket.connected) return socket;
  socket?.disconnect();

  socket = io(baseUrl(), {
    path: '/realtime',
    transports: ['websocket', 'polling'],
    auth: { token: accessToken },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  socket.on('domain', (event: DomainEventEnvelope) => {
    for (const fn of listeners) {
      try {
        fn(event);
      } catch (err) {
        // Never let one listener blow up the others.
        console.error('realtime listener error', err);
      }
    }
  });

  return socket;
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
}

export function onDomainEvent(fn: (event: DomainEventEnvelope) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getRealtimeStatus(): 'connected' | 'disconnected' {
  return socket?.connected ? 'connected' : 'disconnected';
}
